import {
    IAccountInfoResponseData,
    TAccountServiceUserLoginSuccess,
} from '@/core/modules/account/account'
import { AccountsClient, AddUserRequest } from '@core/api/accounts-client'
import { BaseService } from '@core/base/service/base-service'
import { AxiosError } from 'axios'
import {
    accountInfoTransformer,
    transformTeam,
    userLoginSuccessTransformer,
} from '@core/services/api-gateway/account-service/transformers'

export const AccountService = new (class AccountService extends BaseService {
    private readonly client = AccountsClient

    constructor() {
        super('account.service')
    }

    async getAuthUser() {
        const [error, result] = await this.request(() =>
            this.http.get(`/api/auth`)
        )

        if (error) {
            console.log('error auth user', error)
            return false
        }
        return result?.data.auth
    }

    async getAllUserAndAccountData(): Promise<
        TAccountServiceUserLoginSuccess | undefined
    > {
        const [error, result] = await this.request(
            // TODO: Update api, remove account id as mandatory param
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            () => this.client.userLoginSuccess(),
            {
                retry: { maxRetries: 3 },
            }
        )

        if (error) {
            return undefined
        }

        const responseBody = result?.data as {
            data?: {
                expired_accounts: string[]
                users: Record<string, unknown>
                accounts: Record<string, unknown>[]
            }
        }

        const data = responseBody?.data
        if (!data?.users) {
            console.error('getAllUserAndAccountData error')
            return undefined
        }

        if (
            data?.expired_accounts?.length > 0 &&
            data?.accounts?.length === 0
        ) {
            return {
                user: { ...data.users } as TCurrentUser,
                account: '',
                accounts: {},
                expiredAccountsNames: data?.expired_accounts,
                onlyExpiredAccounts: true,
            } as TAccountServiceUserLoginSuccess
        }

        if (!data.accounts) {
            console.error('getAllUserAndAccountData error')
            return undefined
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return userLoginSuccessTransformer(data)
    }

    async getAccountInfo(): Promise<IAccountInfoResponseData | undefined> {
        const [error, result] = await this.request(
            () => this.client.getAccountsInfo(this.accountId),
            {
                retry: { maxRetries: 3 },
            }
        )

        if (error || !result || !result.data.data) {
            return undefined
        }

        const transformedAccountInfo = accountInfoTransformer(result.data.data)

        return transformedAccountInfo
    }

    async getMdrSupportedProducts() {
        const [error, result] = await this.request(() => {
            return this.client.getSupportedMdrProducts(this.accountId)
        })

        if (error || !result) {
            return undefined
        }

        return result.data.data
    }

    async getTeam() {
        const [error, result] = await this.request(() =>
            this.client.getTeamInfo(this.accountId)
        )

        if (error) {
            console.log('error', error)
            return []
        }

        return transformTeam(
            (result?.data?.data?.users as TResponseTeamMember[]) || []
        )
    }

    async deleteUser(userId: string) {
        const [error] = await this.request(() =>
            this.client.deleteUser(this.accountId, userId)
        )

        if (error) {
            console.log('error', error)
            return undefined
        }
        return true
    }

    async setEmailAlerts(userId: string, enabled: boolean) {
        const [error] = await this.request(() =>
            this.client.setEmailAlerts(this.accountId, userId, {
                data: { enable_email_notification: enabled },
            })
        )

        if (error) {
            console.log('error', error)
            return undefined
        }
        return true
    }

    async inviteUser(payload: AddUserRequest) {
        const [error] = await this.request(() =>
            this.client.addUser(this.accountId, payload)
        )

        if (error) {
            console.log('error', error)
            return undefined
        }
        return true
    }

    async newAccountInvitation(email: string) {
        const [error] = await this.request(() =>
            // @ts-ignore
            this.client.userInviteByEmail({ email })
        )

        if (error) {
            this.log('newAccountInvitation failed', {
                status: (error as AxiosError)?.response?.status,
                message: (error as AxiosError)?.message,
            })
            return undefined
        }
        return true
    }

    async getUserMFA() {
        const [error, result] = await this.request(() =>
            this.http.get(`/api/accounts/${this.accountId}/mfa`)
        )

        if (error) {
            console.log('error', error)
            return undefined
        }
        return result?.data.data.mfa_active.data.mfa_active as boolean
    }

    async toggleUserMFA(on: boolean) {
        const [error] = await this.request(() =>
            this.http.post(`/api/accounts/${this.accountId}/mfa`, {
                active: on,
                redirect_to_login: on,
                return_endpoint: on ? location.href : '', // send empty when active false
            })
        )

        if (error) {
            console.log('error', error)
            return undefined
        }

        return true
    }

    async changeAppearance(themeVariant: 'light' | 'dark') {
        const [error] = await this.request(() =>
            this.http.post(
                `/api/accounts/${this.accountId}/users/appearance_mode`,
                {
                    data: { appearance: themeVariant },
                }
            )
        )

        if (error) {
            console.log('error', error)
            return undefined
        }
        return true
    }

    public async activateUser(accountId: string) {
        const [error] = await this.request(() =>
            this.http.post(`/api/activate-user`, { account_id: accountId })
        )

        if (error) {
            console.log('error activate user', error)
            return false
        }

        return true
    }

    public async enforceAccountMfa(value: boolean) {
        const [error] = await this.request(() =>
            this.client.enforceAccountMfa(this.accountId, {
                enable: value,
            })
        )

        if (error) {
            console.log('error', error)
            return undefined
        }

        return true
    }

    async editUser(firstName: string, lastName: string, jobTitle: string) {
        const [error] = await this.request(() =>
            this.http.post(`/api/accounts/${this.accountId}/users/info`, {
                first_name: firstName,
                last_name: lastName,
                job_title: jobTitle,
            })
        )

        if (error) {
            console.log('error', error)
            return undefined
        }

        return true
    }
})()
